import * as stylex from '@stylexjs/stylex';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { ReactElement } from 'react';

import { Breadcrumb, Breadcrumbs, Paragraph, View } from '@/libs/serenity-ui';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { RouterLink } from '@/libs/tanstack-router';

export const Route = createFileRoute('/_main/_details/invite/guide/')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    if (context.user && !context.can.invitation.create()) {
      throw redirect({ to: '/' });
    }
  },
  staticData: {
    details: {
      heading: 'Invite users to TrendMD dashboard',
      slots: {
        topBarComponent: TopBarComponent,
      },
    },
  },
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Invite users',
      },
    ],
  }),
});

function RouteComponent(): ReactElement {
  return (
    <article {...stylex.props(articleStyles.base)}>
      <View style={introSectionStyles.base}>
        <View style={contentStyles.wrapper}>
          <Paragraph color="secondary" size={16} style={contentStyles.lead}>
            In the TrendMD Dashboard, multiple users can manage campaigns. You can invite all of your team and give
            them different roles depending on the tasks they need to perform:
          </Paragraph>
          <ul {...stylex.props(roleListStyles.base)}>
            <li {...stylex.props(roleListStyles.item)}>
              Admin:
              <ul {...stylex.props(roleListStyles.nested)}>
                <li {...stylex.props(roleListStyles.nestedItem)}>Invite users</li>
                <li {...stylex.props(roleListStyles.nestedItem)}>Edit users</li>
                <li {...stylex.props(roleListStyles.nestedItem)}>Manage campaigns</li>
                <li {...stylex.props(roleListStyles.nestedItem)}>Send messages</li>
              </ul>
            </li>
            <li {...stylex.props(roleListStyles.item)}>
              Editor:
              <ul {...stylex.props(roleListStyles.nested)}>
                <li {...stylex.props(roleListStyles.nestedItem)}>Manage campaigns</li>
                <li {...stylex.props(roleListStyles.nestedItem)}>Send messages</li>
              </ul>
            </li>
            <li {...stylex.props(roleListStyles.item)}>Viewer: View campaigns</li>
          </ul>
          <Paragraph color="secondary" size={16} style={contentStyles.lead}>
            To invite a user to the dashboard, take the following steps:
          </Paragraph>
          <ul {...stylex.props(stepsListStyles.base)}>
            <li {...stylex.props(stepsListStyles.item)}>
              In the TrendMD dashboard, in the bottom-left corner, click share icon
              <br />
              This opens the <strong {...stylex.props(textStyles.primary)}>Invite users</strong> window.
            </li>
            <li {...stylex.props(stepsListStyles.item)}>
              Enter the email address to send invitation to.
            </li>
            <li {...stylex.props(stepsListStyles.item)}>Choose a role.</li>
            <li {...stylex.props(stepsListStyles.item)}>
              Click <strong {...stylex.props(textStyles.primary)}>Invite</strong>.
              <br />
              This shows a success message and sends an invitation. The user needs to use the link in the email to
              onboard to the dashboard.
            </li>
          </ul>
        </View>
      </View>
    </article>
  );
}

function TopBarComponent(): ReactElement {
  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/invite/guide">Invite users</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

const articleStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    justifySelf: 'center',
    maxWidth: 728,
    minWidth: 0,
    paddingBottom: 120,
    width: '100%',
  },
});

const introSectionStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
  },
});

const contentStyles = stylex.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  lead: {
    marginBottom: 18,
  },
});

const roleListStyles = stylex.create({
  base: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    margin: 0,
    marginBottom: 18,
    paddingLeft: 20,
  },
  item: {
    marginBottom: 0,
  },
  nested: {
    marginBlock: 0,
    paddingLeft: 20,
  },
  nestedItem: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    listStyleType: 'disc',
  },
});

const stepsListStyles = stylex.create({
  base: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    margin: 0,
    paddingLeft: 20,
  },
  item: {
    marginBottom: 0,
  },
});

const textStyles = stylex.create({
  primary: {
    color: $.textPrimary,
    fontWeight: 600,
  },
});
